import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, scr } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { M11_ROLES, hasAnyRole } from '../../../lib/roles';
import type { Award, Contract, Page, TenderLot, TenderNotice } from '../../../lib/types';
import { useApiQuery } from '../../../lib/useApi';
import { ErrorNote, Field, Spinner, StatusBadge } from '../../../ui/components';

export function AwardTab({ tender, onChanged }: { tender: TenderNotice; onChanged: () => void }) {
  const { persona } = useAuth();
  const isBoard = hasAnyRole(persona?.roles, M11_ROLES.TENDER_BOARD);
  const isOfficer = hasAnyRole(persona?.roles, M11_ROLES.PROCUREMENT_OFFICER);

  const lotsQ = useApiQuery<{ items: TenderLot[] }>(`/api/v1/m11/tenders/${tender.id}/lots`);
  const awardsQ = useApiQuery<Page<Award>>(`/api/v1/m11/awards?tenderId=${tender.id}&limit=50`);
  const contractsQ = useApiQuery<Page<Contract>>('/api/v1/m11/contracts?limit=100');

  const contractFor = (awardId: string) => contractsQ.data?.items.find((c) => c.awardId === awardId);

  return (
    <div className="space-y-5">
      {isBoard && tender.status === 'EVALUATION' && tender.evaluationStartedAt ? (
        <ProposeAward tender={tender} lots={lotsQ.data?.items ?? []} onProposed={() => { awardsQ.reload(); onChanged(); }} />
      ) : null}

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Awards on this tender</h2>
        {awardsQ.error ? <ErrorNote message={awardsQ.error} /> : null}
        {awardsQ.loading ? (
          <Spinner />
        ) : (awardsQ.data?.items.length ?? 0) === 0 ? (
          <p className="text-xs text-slate-400">No award proposed yet.</p>
        ) : (
          <ul className="space-y-3">
            {awardsQ.data!.items.map((a) => (
              <li key={a.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <Link to={`/procurement/awards/${a.id}`} className="font-mono text-sm font-semibold text-brand-600 hover:underline">{a.refNo}</Link>
                  <StatusBadge status={a.status} />
                </div>
                <div className="mt-1 text-xs text-slate-500">Supplier {a.supplierId.slice(0, 14)}… · {scr(a.amountScr)}{a.lotId ? ` · lot ${a.lotId.slice(0, 8)}…` : ''}</div>
                <AwardActions
                  award={a}
                  isBoard={isBoard}
                  isOfficer={isOfficer}
                  contract={contractFor(a.id)}
                  onChanged={() => { awardsQ.reload(); contractsQ.reload(); onChanged(); }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ProposeAward({ tender, lots, onProposed }: { tender: TenderNotice; lots: TenderLot[]; onProposed: () => void }) {
  const [lotId, setLotId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const propose = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m11/tenders/${tender.id}/propose-award`, { lotId: lotId || undefined });
      onProposed();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not propose the award');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Propose award</h2>
      <p className="mb-3 text-xs text-slate-500">Proposes the top-ranked bid from the finalized evaluation report as the award.</p>
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="flex items-end gap-2">
        {lots.length > 0 ? (
          <Field label="Lot" className="!mb-0">
            <select className="field-input" value={lotId} onChange={(e) => setLotId(e.target.value)}>
              <option value="">Choose a lot…</option>
              {lots.map((l) => <option key={l.id} value={l.id}>Lot {l.lotNo} — {l.title}</option>)}
            </select>
          </Field>
        ) : null}
        <button className="btn-primary" onClick={propose} disabled={busy || (lots.length > 0 && !lotId)}>{busy ? 'Proposing…' : 'Propose award'}</button>
      </div>
    </div>
  );
}

function AwardActions({ award, isBoard, isOfficer, contract, onChanged }: {
  award: Award;
  isBoard: boolean;
  isOfficer: boolean;
  contract: Contract | undefined;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: 'confirm' | 'finalize' | 'contract') => {
    setBusy(action);
    setError(null);
    try {
      const path = action === 'contract' ? `/api/v1/m11/awards/${award.id}/contract` : `/api/v1/m11/awards/${award.id}/${action}`;
      await api.post(path);
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2">
      {error ? <div className="mb-1"><ErrorNote message={error} /></div> : null}
      <div className="flex flex-wrap gap-1.5">
        {isBoard && award.status === 'PROPOSED' ? (
          <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('confirm')} disabled={busy !== null}>{busy === 'confirm' ? 'Confirming…' : 'Confirm'}</button>
        ) : null}
        {(isBoard || isOfficer) && award.status === 'CONFIRMED' ? (
          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('finalize')} disabled={busy !== null}>{busy === 'finalize' ? 'Finalizing…' : 'Finalize (needs standstill cleared, no open appeal)'}</button>
        ) : null}
        {isOfficer && award.status === 'FINAL' && !contract ? (
          <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act('contract')} disabled={busy !== null}>{busy === 'contract' ? 'Signing…' : 'Sign contract'}</button>
        ) : null}
        {contract ? (
          <Link to={`/procurement/contracts/${contract.id}`} className="btn-secondary !px-2.5 !py-1 text-xs">
            View contract {contract.refNo} ↗
          </Link>
        ) : null}
      </div>
    </div>
  );
}
